import { DEFAULT_VOICE_ID } from "@/utils/agencies/voices";
import { ANY_UUID_REGEX, ELEVENLABS_VOICE_ID_REGEX } from "@/utils/library/regexes";

export enum Partners {
  AgencyZoom = "Agency Zoom",
}

export enum AgencyCoverage {
  PersonalAutoInsurance = "Auto Insurance",
  PersonalDisabilityInsurance = "Disability Insurance",
  PersonalEarthquakeInsurance = "Earthquake Insurance",
  PersonalFloodInsurance = "Flood Insurance",
  CommercialGeneralLiabilityInsurance = "General Liability Insurance",
  PersonalHealthInsurance = "Health Insurance",
  PersonalHomeownersInsurance = "Homeowners Insurance",
  PersonalIdentityTheftInsurance = "Identity Theft Insurance",
  PersonalLifeInsurance = "Life Insurance",
  PersonalPetInsurance = "Pet Insurance",
  PersonalRentersInsurance = "Renters Insurance",
  PersonalToyInsurance = "Toy Insurance",
  PersonalTravelInsurance = "Travel Insurance",
  PersonalUmbrellaInsurance = "Umbrella Insurance",
  PersonalValuablePropertyInsurance = "Valuable Personal Property Insurance",
  PersonalWeddingInsurance = "Wedding Insurance",
  CommercialPropertyInsurance = "Property Insurance",
  CommercialProfessionalLiabilityInsurance = "Professional Liability Insurance",
  CommercialWorkersCompensationInsurance = "Workers Compensation Insurance",
  CommercialAutoInsurance = "Commercial Auto Insurance",
  CommercialBusinessOwnersPolicyInsurance = "Business Owners Policy Insurance",
  CommercialCyberLiabilityInsurance = "Cyber Liability Insurance",
  CommercialDirectorsAndOfficersInsurance = "Directors and Officers Insurance",
  CommercialConstructionInsurance = "Construction Insurance",
  CommercialMarineInsurance = "Marine Insurance",
  CommercialSuretyBondsInsurance = "Surety Bonds Insurance",
  CommercialProductRecallInsurance = "Product Recall Insurance",
  CommercialProductLiabilityInsurance = "Product Liability Insurance",
  CommercialEnvironmentalAndPollutionLiabilityInsurance = "Environmental and Pollution Liability Insurance",
  CommercialEventCancellationInsurance = "Event Cancellation Insurance",
  CommercialAviationInsurance = "Aviation Insurance",
  CommercialFineArtAndCollectiblesInsurance = "Fine Art and Collectibles Insurance",
  CommercialSportsAndEntertainmentInsurance = "Sports and Entertainment Insurance",
  CommercialAgricultureInsurance = "Agriculture Insurance",
  OtherEmploymentPracticesLiabilityInsurance = "Employment Practices Liability Insurance",
  OtherFidelityAndCrimeInsurance = "Fidelity and Crime Insurance",
  OtherKidnapAndRansomInsurance = "Kidnap and Ransom Insurance",
  OtherLiveScanBackgroundChecks = "Live Scan Background Checks",
  OtherDMVServicesTagRenewalsAndTransfers = "DMV Services: Tag Renewals and Transfers",
  OtherNotaryPublic = "Notary Public",
}

export enum AgencyCoverageSection {
  Personal = "Personal",
  Commercial = "Commercial",
  Other = "Other",
}

export enum AgencyContactInfoType {
  Sales = "Sales",
  Support = "Support",
  Billing = "Billing",
  Claims = "Claims",
  DirectLine = "Direct Line",
  Other = "Other",
}

export enum AgencyContactDepartments {
  Main = "Main",
  Sales = "Sales",
  Support = "Support",
  Billing = "Billing",
  Claims = "Claims",
}

export enum AgencyMaxFaqs {
  GailBrain = 100,
  Scripts = 25,
}

export enum AgencySmartSetupSteps {
  Content = "Content",
  Brain = "Brain",
}

export enum AgencyVerificationStatus {
  Pending = "pending",
  Verified = "verified",
  Failed = "failed",
  NotVerified = "notVerified",
}

export enum SmartSetupStatus {
  Pending = "pending",
  Processed = "processed",
  Completed = "completed",
  Failed = "failed",
}

export enum AgencyDirectoryPhoneType {
  Mobile = "mobile",
  Office = "office",
  Fax = "fax",
}

export enum AgencyDirectoryTransferType {
  Never = "never",
  Always = "always",
  BusinessHours = "business_hours",
  AfterHours = "after_hours",
}

export enum AgencyType {
  Insurance = "insurance",
  Other = "other",
}

export enum VoiceAge {
  YOUNG = "young",
  MIDDLE_AGED = "middle_aged",
}

export enum VoiceGender {
  MALE = "male",
  FEMALE = "female",
}

export enum VoiceAccent {
  AMERICAN = "american",
  AMERICAN_SOUTHERN = "american_southern",
  AFRICAN_AMERICAN = "african_american",
  MEXICAN = "mexican",
  COLOMBIAN = "colombian",
  DOMINICAN = "dominican",
  ECUADORIAN = "ecuadorian",
  ARGENTINIAN = "argentinian",
  BRITISH = "british",
  PERUVIAN = "peruvian",
  PANAMANIAN = "panamanian",
  PARAGUAYAN = "paraguayan",
  CHILEAN = "chilean",
  BRAZILIAN = "brazilian",
  SPAIN = "spain",
  COSTA_RICAN = "costa_rican",
  EL_SALVADORAN = "el_salvadoran",
  GUATEMALAN = "guatemalan",
  HONDURAN = "honduran",
  NICARAGUAN = "nicaraguan",
  PUERTO_RICAN = "puerto_rican",
  URUGUAYAN = "uruguayan",
  VENEZUELAN = "venezuelan",
  CANADIAN = "canadian",
  INDIAN = "indian",
  GERMAN = "german",
  CHINESE = "chinese",
  FRENCH = "french",
  ITALIAN = "italian",
  JAPANESE = "japanese",
  RUSSIAN = "russian",
  KOREAN = "korean",
  CATALAN = "catalan",
  POLISH = "polish",
  NORWEGIAN = "norwegian",
  ESTONIAN = "estonian",
  LITHUANIAN = "lithuanian",
}

export enum VoiceProvider {
  ElevenLabs = "elevenlabs",
  Cartesia = "cartesia",
  OpenAI = "openai",
  Gemini = "gemini",
}

export const InferVoiceProviderFromVoiceId = (voiceId: string | undefined): VoiceProvider => {
  const voiceIdToCheck = voiceId || DEFAULT_VOICE_ID;

  if (ELEVENLABS_VOICE_ID_REGEX.test(voiceIdToCheck)) {
    return VoiceProvider.ElevenLabs;
  }

  return ANY_UUID_REGEX.test(voiceIdToCheck)
    ? VoiceProvider.Cartesia
    : VoiceProvider.OpenAI;
};

export const VOICE_PROVIDER_LABELS: Record<VoiceProvider, string> = {
  [VoiceProvider.ElevenLabs]: "ElevenLabs",
  [VoiceProvider.Cartesia]: "Cartesia",
  [VoiceProvider.OpenAI]: "OpenAI",
  [VoiceProvider.Gemini]: "Gemini",
};

export const VOICE_PROVIDER_VOICE_ID_LABELS: Record<VoiceProvider, string> = {
  [VoiceProvider.ElevenLabs]: "ElevenLabs Voice ID",
  [VoiceProvider.Cartesia]: "Cartesia Voice ID",
  [VoiceProvider.OpenAI]: "OpenAI Voice ID",
  [VoiceProvider.Gemini]: "Gemini Voice ID",
};

export enum AccountLevel {
  SMB = "smb",
  Enterprise = "enterprise",
}

export enum AccountType {
  Insurance = "insurance",
  DebtCollections = "debt_collections",
}

export enum VoiceSpeed {
  SLOWER = "slower",
}
